'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useMemo, useState } from 'react';
import { EntityPicker } from '../../forms/entity-picker/index.js';
import { cx } from '../../../internal/cx.js';
import { ChevronsGlyph, LayersGlyph, PlusGlyph, SettingsGlyph, } from '../../../internal/shell/icons.js';
import { TipButton } from '../../../internal/shell/tip-button.js';
const DEFAULT_MESSAGES = {
    label: 'Space',
    all: 'All spaces',
    search: 'Search spaces…',
    empty: 'No spaces match.',
    loading: 'Loading spaces…',
    manage: 'Manage Spaces',
    create: 'New Space',
    newTab: 'opens in a new tab',
};
/**
 * Chooses the Space (a department-like slice of a workspace) the app shows:
 * "All spaces" or one Space. A popover with search, the current Space checked,
 * and New / Manage links; the list is a listbox with arrow keys, Home/End and
 * type-to-search (EntityPicker). The product supplies the Spaces and stores
 * the choice; the switcher never fetches.
 */
export const SpaceSwitcher = forwardRef(function SpaceSwitcher({ spaces, value: valueProp, defaultValue = null, onValueChange, allowAll = true, manageHref, manageExternal = true, createHref, onCreate, loading = false, collapsed = false, hideWhenEmpty = true, align = 'start', open, onOpenChange, className, messages: messagesProp, }, ref) {
    const messages = useMemo(() => ({ ...DEFAULT_MESSAGES, ...messagesProp }), [messagesProp]);
    const [inner, setInner] = useState(defaultValue);
    const raw = valueProp !== undefined ? valueProp : inner;
    // A chosen Space that no longer exists (deleted, access removed) reads as "All spaces".
    const current = raw ? spaces.find((s) => s.id === raw) : undefined;
    const value = current ? current.id : null;
    if (hideWhenEmpty && spaces.length === 0 && !loading)
        return null;
    const options = spaces.map((s) => ({
        value: s.id,
        label: s.name,
        ...(s.description ? { description: s.description } : {}),
        ...(s.icon ? { media: s.icon } : {}),
    }));
    const name = current?.name ?? messages.all;
    const choose = (next) => {
        if (next === null && !allowAll)
            return;
        if (valueProp === undefined)
            setInner(next);
        onValueChange?.(next);
    };
    const showFooter = Boolean(manageHref || createHref || onCreate);
    return (_jsx(EntityPicker, { "aria-label": messages.label, options: options, value: value, onValueChange: choose, loading: loading, emptyMessage: messages.empty, searchPlaceholder: messages.search, messages: { loading: messages.loading }, align: align, ...(allowAll ? { clearOption: { label: messages.all, media: _jsx(LayersGlyph, {}) } } : {}), ...(open !== undefined ? { open } : {}), ...(onOpenChange ? { onOpenChange } : {}), contentClassName: "mn-space-switcher__content", trigger: _jsxs(TipButton, { ref: ref, className: cx('mn-space-switcher', className), "data-collapsed": collapsed ? '' : undefined, "data-all": current ? undefined : '', 
            // Named by its text (kept for screen readers when collapsed), never by an
            // aria-label, so the visible text is always part of the name.
            tip: collapsed ? `${messages.label}: ${name}` : undefined, children: [_jsx("span", { className: "mn-space-switcher__icon", "aria-hidden": true, children: current?.icon ?? _jsx(LayersGlyph, {}) }), _jsxs("span", { className: "mn-space-switcher__text", children: [_jsx("span", { className: "mn-space-switcher__caption", "aria-hidden": true, children: messages.label }), _jsxs("span", { className: "mn-visually-hidden", children: [messages.label, ": "] }), _jsx("span", { className: "mn-space-switcher__name", children: name })] }), _jsx(ChevronsGlyph, { className: "mn-space-switcher__chevron" })] }), footer: showFooter
            ? ({ close }) => (_jsxs("div", { className: "mn-space-switcher__footer", children: [onCreate ? (_jsxs("button", { type: "button", className: "mn-space-switcher__action", onClick: () => {
                            close();
                            onCreate();
                        }, children: [_jsx(PlusGlyph, {}), messages.create] })) : createHref ? (_jsxs("a", { className: "mn-space-switcher__action", href: createHref, children: [_jsx(PlusGlyph, {}), messages.create] })) : null, manageHref ? (_jsxs("a", { className: "mn-space-switcher__action", href: manageHref, ...(manageExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {}), children: [_jsx(SettingsGlyph, {}), messages.manage, manageExternal ? (_jsxs("span", { className: "mn-visually-hidden", children: [" (", messages.newTab, ")"] })) : null] })) : null] }))
            : undefined }));
});
//# sourceMappingURL=space-switcher.js.map